import { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { getDatabase, ref, get, update } from 'firebase/database';
import '../pagesCSS/profile.css';
import '../pagesCSS/profile.mobile.css';
import Swal from 'sweetalert2';

const Profile = () => {
  const auth = getAuth();
  const db = getDatabase();

  // User info state
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('User');
  const [email, setEmail] = useState('');
  const [accountCreationDate, setAccountCreationDate] = useState('');
  const [tagline, setTagline] = useState('Coding my way up — aspiring techie on a mission 🌸');
  const [preferredContact, setPreferredContact] = useState('');
  const [currentYear, setCurrentYear] = useState('');
  const [gpa, setGpa] = useState('');
  const [viewMode, setViewMode] = useState('regular');
  const [isEditing, setIsEditing] = useState(false);
  
  //migrate info
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [oldEmail, setOldEmail] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState('');
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState('');
  const [verificationCheckInterval, setVerificationCheckInterval] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  
  const escapeEmail = (email) => {
    return email.replace(/\./g, ',');
  };

  const resendEmailVerification = async () => {
    if (auth.currentUser && !resendDisabled) {
        try {
            setEmailVerified(true);
            await sendEmailVerification(auth.currentUser);
            startTimer();
            setError('A new verification email has been sent. Please check your inbox.');
        } catch (error) {
            console.error("Error resending verification email:", error);
            setError('Error sending verification email. Please try again later.');
        }
    }
  };

  const startTimer = () => {
    setResendDisabled(true);
    setTimer(40);
    const interval = setInterval(() => {
      setTimer(prevTime => {
          if (prevTime <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              return 0;
          }
          return prevTime - 1;
      });
    }, 1000);
  };

  useEffect(() => {
      return () => {
          if (verificationCheckInterval) {
              clearInterval(verificationCheckInterval);
          }
      };
  }, [verificationCheckInterval]);

  const handleMigrateData = async () => {
    if (email == oldEmail) {
      setMigrationStatus('Cannot transfer account data to current account.');
      return;
    }
    if (!oldEmail.endsWith('@ufl.edu')) {
      setMigrationStatus('Please enter a valid @ufl.edu email.');
      return;
    }
    //verify that's the user account
     if (emailVerified) {
        await sendEmailVerification(email);
        setEmailSent(true);
        startTimer();
        setError('Please verify your email address before signing in.');
    } else {
        try {
        const escapedOldEmail = escapeEmail(oldEmail);
        const uidRef = ref(db, `emailToUid/${escapedOldEmail}`);
        const uidSnapshot = await get(uidRef);

        if (!uidSnapshot.exists()) {
          setMigrationStatus("We couldn't find an account with that email.");
          return;
        }

        const oldUid = uidSnapshot.val();
        const oldUserRef = ref(db, `users/${oldUid}`);
        const newUserRef = ref(db, `users/${user.uid}`);

        const oldUserSnap = await get(oldUserRef);
        if (!oldUserSnap.exists()) {
          setMigrationStatus("Old user data not found.");
          return;
        }

        const oldData = oldUserSnap.val();
        await update(newUserRef, {
          ...oldData,
          email: user.email // preserve the new email
        });

        setMigrationStatus("Data successfully transferred!");
      } catch (error) {
        console.error("Migration failed:", error);
        setMigrationStatus("Something went wrong during migration.");
      }
    }
  };


  // Load user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userId = currentUser.uid;
        try {
          const userRef = ref(db, `users/${userId}`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            const userData = snapshot.val();
            setUsername(userData.username || 'User');
            setEmail(userData.email || '');
            setAccountCreationDate(
              userData.createdAt
                ? new Date(userData.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : ''
            );
            setTagline(userData.tagline || 'Aspiring software engineer on a mission 🌸');
            setPreferredContact(userData.preferredContact || '');
            setCurrentYear(userData.currentYear || '');
            setGpa(userData.gpa || '');
          }
        } catch (error) {
          console.error('Error fetching user:', error);
        }
      } else {
        setUser(null);
        setUsername('User');
      }
    });
    return () => unsubscribe();
  }, [auth, db]);

  // Save updated data to Firebase
  const handleSave = async () => {
    if (!user) return;

    const userId = user.uid;
    const userRef = ref(db, `users/${userId}`);

    try {
      await update(userRef, {
        username,
        email,
        tagline,
        preferredContact,
        currentYear,
        gpa,
      });
      setIsEditing(false);
      Swal.fire({
        title: "Profile Updated!",
        icon: "success"
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        title: "Failed to update profile",
        icon: "error"
      });
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-avatar">
          <img
            src={`https://api.dicebear.com/7.x/thumbs/svg?seed=${username}`}
            alt="avatar"
          />
        </div>

        {/* EDITING VIEW */}
        {viewMode === 'edit' && (
          <>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="profile-input"
              placeholder="Username"
            />
            <textarea
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              className="profile-textarea"
              placeholder="Tagline"
              rows={2}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="profile-input"
              placeholder="Email"
            />
            <input
              type="text"
              value={preferredContact}
              onChange={(e) => setPreferredContact(e.target.value)}
              className="profile-input"
              placeholder="Main email address / Phone Number"
            />
            <input
              type="text"
              value={currentYear}
              onChange={(e) => setCurrentYear(e.target.value)}
              className="profile-input"
              placeholder="Current Year (e.g. Sophomore, Junior)"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              max="4"
              value={gpa}
              onChange={(e) => setGpa(e.target.value)}
              className="profile-input"
              placeholder="GPA"
            />
            <p><strong>Joined:</strong> {accountCreationDate}</p>

            <div className="profile-buttons">
              <button onClick={handleSave} className="btn btn-primary">Save</button>
              <button onClick={() => setViewMode('regular')} className="btn btn-secondary">Cancel</button>
            </div>
          </>
        )}

        {/* TRANSFER VIEW */}
        {viewMode === 'transfer' && (
          <div className="migration-prompt">
            <p className="pstyle1">Did you previously use a @ufl.edu email account?</p>
            <input
              type="email"
              className="input-field"
              placeholder="Enter your old UF email"
              value={oldEmail}
              onChange={(e) => setOldEmail(e.target.value)}
            />
            <button className="continue-btn" onClick={handleMigrateData}>
              Transfer Data from Old Account
            </button>
            {/* {emailSent ?
                <button className="continue-btn" onClick={resendEmailVerification} disabled={resendDisabled}>
                    Resend Email Verification ({timer}s)
                </button> :
                 <button className="continue-btn" onClick={handleMigrateData}>Continue</button>
            } */}
            {migrationStatus && <p className="pstyle1">{migrationStatus}</p>}
            <button onClick={() => setViewMode('regular')} className="btn btn-edit">Back to Profile</button>
          </div>
        )}

        {/* REGULAR VIEW */}
        {viewMode === 'regular' && (
          <>
            <h1 className="profile-name">{username}</h1>
            <p className="profile-tagline">{tagline}</p>
            <div className="profile-info">
              <p><strong>Email:</strong> {email || '—'}</p>
              <p><strong>Preferred Contact:</strong> {preferredContact || '—'}</p>
              <p><strong>Current School Year:</strong> {currentYear || '—'}</p>
              <p><strong>GPA:</strong> {gpa || '—'}</p>
              <p><strong>Joined:</strong> {accountCreationDate}</p>
            </div>
            <button onClick={() => setViewMode('edit')} className="btn btn-edit">Edit Profile</button>
            <button onClick={() => setViewMode('transfer')} className="btn btn-edit">Transfer Account Data</button>
          </>
        )}
      </div>
    </div>

  );
};

export default Profile;
